import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

const API_BASE = environment.apiUrl;

// ── Competition Modes ────────────────────────────────────────────────────────
export interface CompetitionMode {
  id: number;
  name: string;
  code: 'beat_the_player' | 'free_player_assessment';
  description: string;
}
export interface CompetitionModesResponse {
  success: boolean;
  data: CompetitionMode[];
}

// ── Player Stats ──────────────────────────────────────────────────────────────
export interface BusinessRuleStat {
  business_rule_id: number;
  business_rule_name: string;
  current_skill_level: {
    id: number;
    skill_level: string;   // "10.000000" — scale 0-10
    evaluated_events: number;
    // K-weighted counterpart of skill_level, computed from THIS rule's own
    // evaluated_events (reps) against its own K — same formula/spirit as
    // weighted_score, just applied per business rule instead of per drill,
    // since each rule already carries its own rep count. Optional so this
    // interface still works against payloads that haven't added it yet.
    weighted_skill_level?: string;
  };
}

export interface EventTypeStat {
  event_type_id: number;
  event_type_name: string;
  average_skill_level: number;
  // K-weighted counterpart of average_skill_level — same basis as the
  // guest's per-drill weighted_score, so a reference player's drill numbers
  // and a guest's drill numbers are directly comparable. Optional so this
  // interface still works against the guest-result endpoint, which may not
  // always send it.
  weighted_average_skill_level?: number;
  drill_video_url?: string | null;  // video shown during live drill reps
  business_rules: BusinessRuleStat[];
}

// ── Player Video ──────────────────────────────────────────────────────────────
export interface ApiPlayerVideo {
  id: number;
  title: string;
  url: string;
}

// ── Player ───────────────────────────────────────────────────────────────────
export interface ApiPlayer {
  id: number;
  first_name: string;
  last_name: string;
  display_name: string;
  gender: string;
  sport: string;
  ranking_label: string;
  bio: string | null;
  image_url: string | null;
  school_grade_from_id: number;
  school_grade_to_id: number;
  stats: EventTypeStat[];
  video_urls?: ApiPlayerVideo[];
  overall_average_skill_level?: number;
  // K-weighted counterpart — same field name/shape already used on the
  // player object returned by getGuestResult() (see below), applied here
  // too so the "Beat X.XX" score shown BEFORE competing (player-select,
  // tv-player-selected, tv-demo, tv-tutorial, tv-live-drill target score,
  // etc.) matches the weighted number shown AFTER competing.
  weighted_overall_average_skill_level?: number;

  // Pending — ask backend to add
  score_to_beat?: number;
  color?: string;
  jersey?: string;
}

export interface PlayerCompetitionResponse {
  success: boolean;
  data: ApiPlayer;
}
export interface PlayerCompetitionParams {
  gender: 'boys' | 'girls';
  school_grade_from_id: number;
  school_grade_to_id: number;
  sport: string;
}

// ── Sessions ─────────────────────────────────────────────────────────────────
export interface CreateSessionRequest {
  competition_mode_id: number;
  player_id: number;
  guest_id: number;
  gender: 'boys' | 'girls';
  sport: string;
  school_grade_from_id: number;
  school_grade_to_id: number;
}
export interface CreateSessionResponse {
  success: boolean;
  data: { id: string; session_id: string };
}

// ── Events ───────────────────────────────────────────────────────────────────
export interface CreateEventRequest {
  type: 'drill_start' | 'drill_score' | 'drill_complete';
  drill_index: number;
  score?: number;
  total_score?: number;
  timestamp?: number;
}

// ── Close Session ─────────────────────────────────────────────────────────────
export interface CloseSessionRequest {
  total_score: number;
  won: boolean;
  drill_scores: number[];
  athlete_name?: string;
  email?: string;
}
export interface CloseSessionResponse {
  success: boolean;
  data: { success: boolean };
}

// ── Guest (the participant playing) ──────────────────────────────────────────
export interface CreateGuestRequest {
  gender: 'boys' | 'girls';
  school_grade_from_id: number;
  school_grade_to_id: number;
  height_feet: number;
  height_inches: number;
  stand_player_id?: number;  // player the guest is competing against
  competition_mode: 'beat_the_player' | 'free_player_assessment';
}
export interface ApiGuest {
  id: number;
  person_type: 'guest';
  session_code: string;
  display_name: string;
  gender: string;
  school_grade_from_id: number;
  school_grade_to_id: number;
  height: number;
  status: string;
}
export interface CreateGuestResponse {
  success: boolean;
  data: ApiGuest;
}

// ── Drills ────────────────────────────────────────────────────────────────────
export interface ApiDrill {
  id: number;
  name: string;
  set_time: number;
  rounds: number;
  selected_hand: string;
  audio_instructions: string | null;
  is_booth: boolean;
  media_item?: {
    id: number;
    url: string;
    thumbnail_url: string;
    duration_seconds: number;
  } | null;
}
export interface DrillsResponse {
  success: boolean;
  data: ApiDrill[];
}

// ── Service ──────────────────────────────────────────────────────────────────
@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);

  /** GET /stand/competition-modes */
  getCompetitionModes(): Observable<CompetitionModesResponse> {
    return this.http.get<CompetitionModesResponse>(`${API_BASE}/stand/competition-modes`);
  }

  /** GET /stand/players/competition */
  getPlayerToCompete(params: PlayerCompetitionParams): Observable<PlayerCompetitionResponse> {
    const p = new HttpParams()
      .set('gender', params.gender)
      .set('school_grade_from_id', params.school_grade_from_id)
      .set('school_grade_to_id', params.school_grade_to_id)
      .set('sport', params.sport);
    return this.http.get<PlayerCompetitionResponse>(
      `${API_BASE}/stand/players/competition`, { params: p }
    );
  }

  /** POST /stand/sessions */
  createSession(body: CreateSessionRequest): Observable<CreateSessionResponse> {
    return this.http.post<CreateSessionResponse>(`${API_BASE}/stand/sessions`, body);
  }

  /** POST /stand/sessions/{id}/events */
  createEvent(sessionId: string, body: CreateEventRequest): Observable<void> {
    return this.http.post<void>(
      `${API_BASE}/stand/sessions/${sessionId}/events`, body
    );
  }

  /** POST /stand/sessions/{id}/close */
  closeSession(sessionId: string, body: CloseSessionRequest): Observable<CloseSessionResponse> {
    return this.http.post<CloseSessionResponse>(
      `${API_BASE}/stand/sessions/${sessionId}/close`, body
    );
  }

  /** GET /stand/drills?competition_type=beat_the_player|free_player_assessment */
  getDrills(competitionType?: string): Observable<DrillsResponse> {
    let params = new HttpParams();
    if (competitionType) params = params.set('competition_type', competitionType);
    return this.http.get<DrillsResponse>(`${API_BASE}/stand/drills`, { params });
  }

  /** POST /stand/guest */
  createGuest(body: CreateGuestRequest): Observable<CreateGuestResponse> {
    return this.http.post<CreateGuestResponse>(`${API_BASE}/stand/guest`, body);
  }

  /** GET /stand/players/featured */
  getFeaturedPlayers(): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(
      `${API_BASE}/stand/players/featured`
    );
  }

  /** GET /stand/guest/{id}/result */
  getGuestResult(guestId: number): Observable<{
    success: boolean;
    data: {
      guest: { id: number; display_name: string; first_name: string };
      player: {
        id: number;
        display_name: string;
        overall_average_skill_level: number;
        weighted_overall_average_skill_level?: number;
      } | null;
      drill_scores: {
        event_type_id: number;
        event_type_name: string;
        score: number;
        weighted_score?: number;
        weighted_stars?: number;
      }[];
      average_score: number;
      weighted_average_score?: number;
      won: boolean;
      // Real per-drill business-rule breakdown (from buildPlayerStats), 0-10 scale
      stats?: EventTypeStat[];
    };
  }> {
    return this.http.get<any>(`${API_BASE}/stand/guest/${guestId}/result`);
  }

  /** POST /stand/guest/{id}/compile-video */
  compileVideo(guestId: number): Promise<{ success: boolean; video_url?: string; message?: string }> {
    return firstValueFrom(
      this.http.post<{ success: boolean; video_url?: string; message?: string }>(
        `${API_BASE}/stand/guest/${guestId}/compile-video`, {}
      )
    );
  }

  /** GET /stand/leaderboard */
  getLeaderboard(): Observable<{ success: boolean; data: { name: string; score: number }[] }> {
    return this.http.get<{ success: boolean; data: { name: string; score: number }[] }>(
      `${API_BASE}/stand/leaderboard`
    );
  }
  updateGuest(id: number, body: { first_name?: string; last_name?: string; email?: string; phone?: string }): Promise<any> {
    return firstValueFrom(
      this.http.put(`${API_BASE}/stand/guest/${id}`, body)
    );
  }
}
